import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import Logo from "../components/Logo";
import useAuthStore from "../store/authStore";
import { Eye, EyeOff, Loader2, ArrowRight, Mail, Lock } from "lucide-react";
import GoogleSignInButton from "../components/GoogleSignInButton";
import { toast } from "sonner";
import api from "../services/api";
import OtpVerify from "../components/OtpVerify";

export default function Login() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [stats, setStats] = useState(null);
  // Sprint-84 — login is 2FA-gated. When the backend returns
  // `otp_required:true` we transition to the OTP verification screen
  // instead of routing to the dashboard.
  const [otpStep, setOtpStep] = useState(null);   // { loginToken, emailMasked, resendAfter }
  const { login } = useAuthStore();
  const navigate = useNavigate();

  useEffect(() => {
    api.get("/public/stats").then(({ data }) => setStats(data)).catch(() => {});
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await login(email, password);
      if (res && res.otp_required) {
        setOtpStep({
          loginToken: res.login_token,
          emailMasked: res.email_masked,
          resendAfter: res.resend_after_s || 60,
        });
        toast.message(`Verification code sent to ${res.email_masked}`);
        return;
      }
      // Bypass mode — already signed in
      if (res && res.must_change_password) {
        navigate("/force-password-change");
        return;
      }
      toast.success("Welcome back!");
      navigate("/dashboard");
    } catch (err) {
      toast.error(err.response?.data?.detail || err?.message || "Invalid credentials");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 flex" data-testid="login-page">
      {/* Left panel - branding */}
      <div className="hidden lg:flex lg:w-[45%] bg-[#0B132B] relative overflow-hidden items-center justify-center p-12">
        <div className="absolute inset-0 bg-gradient-to-br from-[#FF6B00]/15 to-transparent pointer-events-none" />
        <div className="absolute -bottom-20 -left-20 w-[400px] h-[400px] bg-[#FF6B00]/10 rounded-full blur-3xl" />
        <div className="relative z-10 max-w-md">
          <div className="flex items-center gap-3 mb-10">
            <Logo size="lg" textColor="text-white" />
          </div>
          <h2 className="font-['Outfit'] text-3xl font-bold text-white leading-tight mb-4">
            Deliver anything,<br/>anywhere in minutes
          </h2>
          <p className="text-slate-400 text-base leading-relaxed">
            Join thousands of businesses and individuals who trust PickNGo for fast, reliable logistics.
          </p>
          <div className="mt-10 grid grid-cols-3 gap-6">
            <div>
              <p className="font-['Outfit'] text-2xl font-bold text-white">{stats ? `${stats.deliveries_completed}+` : "..."}</p>
              <p className="text-xs text-slate-400 mt-1">Deliveries</p>
            </div>
            <div>
              <p className="font-['Outfit'] text-2xl font-bold text-white">{stats ? `${stats.on_time_rate}%` : "..."}</p>
              <p className="text-xs text-slate-400 mt-1">On-Time</p>
            </div>
            <div>
              <p className="font-['Outfit'] text-2xl font-bold text-white">{stats ? `${stats.cities_covered}` : "..."}</p>
              <p className="text-xs text-slate-400 mt-1">Cities</p>
            </div>
          </div>
        </div>
      </div>

      {/* Right panel - form */}
      <div className="flex-1 flex items-center justify-center p-6 sm:p-12">
        <div className="w-full max-w-md animate-fadeInUp">
          {/* Mobile logo */}
          <div className="lg:hidden flex items-center gap-2.5 mb-10">
            <Logo size="md" />
          </div>

          <div className="mb-8">
            <h1 className="font-['Outfit'] text-2xl sm:text-3xl font-bold text-slate-900">Welcome back</h1>
            <p className="text-sm text-slate-500 mt-2">Sign in to manage your deliveries</p>
          </div>

          {/* Sprint-84 — OTP step swaps the form, in-place. */}
          {otpStep ? (
            <OtpVerify
              loginToken={otpStep.loginToken}
              emailMasked={otpStep.emailMasked}
              resendAfter={otpStep.resendAfter}
              onSuccess={() => navigate("/dashboard")}
              onCancel={() => setOtpStep(null)}
            />
          ) : (<>
          <form onSubmit={handleSubmit} className="space-y-5" data-testid="login-form">
            <div className="space-y-2">
              <Label className="text-sm font-medium text-slate-700">Email or phone number</Label>
              <div className="relative">
                <Mail className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                <Input
                  type="text"
                  inputMode="email"
                  autoComplete="username"
                  placeholder="you@example.com or 9876543210"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="rounded-xl border-slate-200 h-12 pl-10 focus:border-[#FF6B00] focus:ring-[#FF6B00]/20"
                  required
                  data-testid="login-email-input"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label className="text-sm font-medium text-slate-700">Password</Label>
              <div className="relative">
                <Lock className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                <Input
                  type={showPassword ? "text" : "password"}
                  placeholder="Enter your password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="rounded-xl border-slate-200 h-12 pl-10 pr-12 focus:border-[#FF6B00] focus:ring-[#FF6B00]/20"
                  required
                  data-testid="login-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3.5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 transition-colors"
                  data-testid="login-toggle-password"
                >
                  {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </div>
            <Button
              type="submit"
              disabled={loading}
              className="w-full bg-[#FF6B00] text-white hover:bg-[#E65A00] rounded-xl h-12 font-semibold shadow-sm shadow-[#FF6B00]/20 hover:shadow-md transition-all active:scale-[0.98]"
              data-testid="login-submit-button"
            >
              {loading ? <Loader2 className="h-5 w-5 animate-spin mr-2" /> : <ArrowRight className="h-5 w-5 mr-2" />}
              {loading ? "Signing in..." : "Sign In"}
            </Button>

            <GoogleSignInButton redirectTo="/dashboard" />
          </form>

          <p className="text-sm text-center text-slate-500 mt-8" data-testid="login-register-link">
            Don't have an account?{" "}
            <Link to="/register" className="text-[#FF6B00] font-semibold hover:text-[#E65A00] transition-colors">
              Create account
            </Link>
          </p>
          </>)}
        </div>
      </div>
    </div>
  );
}
